/**
 * ServiceApp.ai marketing blog corpus — long-form SEO articles under /blog/:slug.
 * Static + configurable via this module (not T&B Blog.tsx). Super-admin can hide,
 * feature and paginate posts in Platform Admin without changing crawler-stable URLs.
 */

import { SERVICEAPP_BLOG_POSTS_EXTRA } from './serviceappBlogPostsExtra.js';

export interface ServiceAppBlogSection {
  heading: string;
  paragraphs: string[];
}

export interface ServiceAppBlogPost {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  datePublished: string;
  dateModified: string;
  category: string;
  readingMinutes: number;
  heroImage: string;
  heroImageAlt: string;
  excerpt: string;
  relatedLandingSlugs: string[];
  relatedPostSlugs: string[];
  sections: ServiceAppBlogSection[];
  faqs: Array<{ q: string; a: string }>;
}

const IMG = '/serviceapp-hero-dashboard.png';
const IMG_CLEAN = '/serviceapp-owner-cleaning.jpg';
const IMG_BANNER = '/serviceapp-hero-banner.png';

export const SERVICEAPP_BLOG_INDEX_SEO = {
  title: 'ServiceApp Blog | Small Business, Service App & Field Service Guides',
  description:
    'Practical guides on small business software, service apps, field service tools, cleaning software and how to run a service business — from the ServiceApp.ai team.',
  keywords:
    'small business software blog, service app guides, field service tips, how to run a small business, cleaning service software, ServiceApp blog, AI chat service software',
} as const;

const SERVICEAPP_BLOG_POSTS_CORE: ServiceAppBlogPost[] = [
  {
    slug: 'best-small-business-software-2026',
    title: 'Best Small Business Software in 2026: What Owners Actually Need',
    description:
      'What makes the best small business software in 2026? A practical buyer guide covering all-in-one systems, pricing traps, AI and why service businesses need more than invoicing tools.',
    keywords:
      'best small business software, small business software 2026, all-in-one small business software, software for small business owners',
    datePublished: '2026-07-15',
    dateModified: '2026-08-03',
    category: 'Small business software',
    readingMinutes: 9,
    heroImage: IMG,
    heroImageAlt: 'Small business owner comparing software options on a dashboard',
    excerpt:
      'Skip the feature bingo. Here is how to choose small business software that actually runs sales, delivery and money — without five subscriptions.',
    relatedLandingSlugs: ['/small-business-software', '/business-app', '/how-to-run-a-small-business'],
    relatedPostSlugs: [
      'how-to-choose-small-business-software',
      'replace-spreadsheets-whatsapp-with-one-system',
      'all-in-one-vs-best-of-breed-software',
    ],
    sections: [
      {
        heading: 'What “best small business software” really means',
        paragraphs: [
          'The best small business software is not the one with the longest feature list. It is the system you open every day because it holds your customers, quotes, schedule, team and invoices in one place. If you still copy data between apps, you do not have an operating system — you have a pile of tools.',
          'Owners who sell services (cleaning, trades, HVAC, pest control, consulting with site visits) need workflows that move from enquiry → quote → job → invoice → payment. Generic “business apps” often stop at CRM or accounting and leave the middle messy.',
        ],
      },
      {
        heading: 'Five must-haves in 2026',
        paragraphs: [
          '1) One customer record that feeds quotes, jobs and invoices. 2) Scheduling that respects real capacity. 3) Mobile access for people in the field. 4) Automatic invoicing when work is done. 5) AI that replies and follows up — not just a chatbot bolted on for marketing.',
          'Also watch pricing: per-seat fees punish growth. Office-user or flat plans with unlimited crew keep software cost aligned with revenue.',
        ],
      },
      {
        heading: 'How ServiceApp.ai fits',
        paragraphs: [
          'ServiceApp.ai is small business software built for owners who quote work, schedule people and get paid. It includes WhatsApp quotes, job scheduling, unlimited crew, tax-ready invoicing, payments and Ava — an AI assistant for enquiries and follow-ups — with a Start from $49/mo.',
          'If you want a deeper product overview, start with our [small business software](/small-business-software) page or the [business app](/business-app) guide.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What is the best small business software for service companies?',
        a: 'Choose software that covers leads, quotes, scheduling, field jobs, invoicing and payments. ServiceApp.ai is built for that workflow with unlimited crew and a Start from $49/mo.',
      },
      {
        q: 'Is free small business software enough?',
        a: 'A free plan is enough to organise an owner-operator. As job volume grows, paid tiers should scale with bookings — not force you onto per-user pricing.',
      },
    ],
  },
  {
    slug: 'how-to-choose-small-business-software',
    title: 'How to Choose Small Business Software (Without Buying the Wrong Stack)',
    description:
      'A step-by-step checklist for choosing small business software: map your workflow, avoid per-seat traps, test mobile use and pick a system your team will actually open.',
    keywords:
      'how to choose small business software, small business software buyer guide, pick business software',
    datePublished: '2026-07-18',
    dateModified: '2026-08-03',
    category: 'Small business software',
    readingMinutes: 8,
    heroImage: IMG,
    heroImageAlt: 'Checklist for choosing small business software',
    excerpt:
      'Map the path from first enquiry to paid invoice before you compare logos. That one exercise eliminates most bad software purchases.',
    relatedLandingSlugs: ['/small-business-software', '/service-software'],
    relatedPostSlugs: ['best-small-business-software-2026', 'all-in-one-vs-best-of-breed-software'],
    sections: [
      {
        heading: 'Start with your workflow, not a feature matrix',
        paragraphs: [
          'Write down what happens when a new lead arrives: who replies, how you quote, how you book the job, who does the work, when you invoice and how you collect payment. Any small business software you buy should cover that chain without exports.',
          'If a product shines at accounting but leaves scheduling in WhatsApp, you will still drown in admin.',
        ],
      },
      {
        heading: 'Questions to ask every vendor',
        paragraphs: [
          'Does pricing charge per user? How fast can we go live? Can field staff use it on a phone without an app-store install? Will quotes and invoices share the same customer data? Is AI included or an expensive add-on?',
          'Run a real trial with last week’s jobs — not a polished demo dataset.',
        ],
      },
      {
        heading: 'A simple decision rule',
        paragraphs: [
          'Pick the system that reduces double entry the most. ServiceApp.ai is designed so approved quotes become jobs and completed jobs become invoices — which is usually where owners lose hours every week. Compare it on our [small business software](/small-business-software) page.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Should I buy separate CRM, scheduling and invoicing tools?',
        a: 'Only if they integrate deeply. For most small service businesses, one platform for the full workflow is faster and cheaper than stitching three tools together.',
      },
    ],
  },
  {
    slug: 'how-to-run-a-small-service-business',
    title: 'How to Run a Small Service Business Day to Day',
    description:
      'A practical operating rhythm for how to run a small service business: morning lead triage, quoting fast, scheduling capacity, invoicing same day and using AI after hours.',
    keywords:
      'how to run a small service business, how to run a small business, run service company, small business operations',
    datePublished: '2026-07-20',
    dateModified: '2026-08-03',
    category: 'Operations',
    readingMinutes: 10,
    heroImage: IMG_BANNER,
    heroImageAlt: 'Service business owner planning the day on phone and laptop',
    excerpt:
      'Running a service business is a systems problem. Here is a daily rhythm that keeps cash and customers from slipping through WhatsApp.',
    relatedLandingSlugs: ['/how-to-run-a-small-business', '/service-business-app', '/service-app'],
    relatedPostSlugs: [
      'replace-spreadsheets-whatsapp-with-one-system',
      'stop-losing-leads-after-hours',
      'invoice-same-day-get-paid-faster',
    ],
    sections: [
      {
        heading: 'Morning: clear the pipeline',
        paragraphs: [
          'Open one list of enquiries — not three chat apps. Assign a next action to every open lead: reply, quote, book or close. Owners who skip this step lose the hottest jobs to slower competitors.',
        ],
      },
      {
        heading: 'Midday: protect capacity',
        paragraphs: [
          'Only book work you can deliver. A shared calendar with recurring jobs prevents double-booking cleaners or technicians. Field teams should see today’s jobs on their phone without calling the office.',
        ],
      },
      {
        heading: 'Evening: invoice what you finished',
        paragraphs: [
          'Same-day invoicing is the highest-ROI habit in a service business. If invoices wait until Sunday, cash waits until next month. Automatic invoices from completed jobs remove the willpower problem.',
          'For a full playbook, see [how to run a small business](/how-to-run-a-small-business) and try ServiceApp.ai as the system underneath the rhythm.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How do I run a small business alone?',
        a: 'Standardise handoffs and automate follow-ups. Capture every lead in one place, quote quickly, schedule honestly and invoice on completion. An AI assistant helps cover after-hours replies.',
      },
    ],
  },
  {
    slug: 'what-is-a-service-app',
    title: 'What Is a Service App? (And Who Needs One)',
    description:
      'A clear definition of a service app: software for businesses that quote, schedule and deliver services. Learn who needs a service business app and what features matter.',
    keywords: 'what is a service app, service app, service business app, service company app',
    datePublished: '2026-07-22',
    dateModified: '2026-08-03',
    category: 'Service app',
    readingMinutes: 7,
    heroImage: IMG_BANNER,
    heroImageAlt: 'Service app on a phone showing today’s jobs',
    excerpt:
      'A service app is not a generic to-do list. It is the operating layer for companies that sell and deliver work at a customer’s location or on a schedule.',
    relatedLandingSlugs: ['/service-app', '/service-business-app', '/service-software'],
    relatedPostSlugs: ['best-service-app-for-small-business', 'field-service-app-vs-fsm-software'],
    sections: [
      {
        heading: 'Definition',
        paragraphs: [
          'A service app helps a business capture demand, quote price, schedule delivery, dispatch people, prove work was done and collect payment. That is different from ecommerce software (products) or pure accounting software (books after the fact).',
        ],
      },
      {
        heading: 'Who needs a service app?',
        paragraphs: [
          'Cleaning companies, pest control, HVAC, plumbing, handyman, landscaping, facility maintenance and any team that books jobs and invoices customers. Solo owners benefit as much as 20-person crews — they just need unlimited crew and simple setup.',
        ],
      },
      {
        heading: 'ServiceApp.ai as a service app',
        paragraphs: [
          'ServiceApp.ai is a service app with WhatsApp quoting, scheduling, mobile job reports, invoicing, payments and AI. Explore the [service app](/service-app) and [service business app](/service-business-app) pages for product detail.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is a service app the same as field service software?',
        a: 'Field service software is a type of service app focused on technicians at customer sites. Many owners search both terms for the same need.',
      },
    ],
  },
  {
    slug: 'best-service-app-for-small-business',
    title: 'Best Service App for Small Business: Buyer Checklist',
    description:
      'How to pick the best service app for a small business — unlimited crew, WhatsApp quotes, mobile jobs, automatic invoicing and fair pricing without per-seat fees.',
    keywords:
      'best service app for small business, best service app, service app for small business, small service business app',
    datePublished: '2026-07-24',
    dateModified: '2026-08-03',
    category: 'Service app',
    readingMinutes: 8,
    heroImage: IMG,
    heroImageAlt: 'Comparing service apps for a small business',
    excerpt:
      'The best service app for a small business is the one your crew will use tomorrow morning — not the enterprise suite with a six-month rollout.',
    relatedLandingSlugs: ['/service-app', '/service-business-app', '/small-business-software'],
    relatedPostSlugs: ['what-is-a-service-app', 'field-service-app-vs-fsm-software'],
    sections: [
      {
        heading: 'Checklist',
        paragraphs: [
          'Unlimited crew, mobile jobs, WhatsApp or SMS quotes, automatic invoices, payment links, reporting and a trial that uses your real services. Bonus: AI that answers after hours.',
          'Avoid tools that charge every technician a seat fee — that model is built for large fleets, not owner-operators.',
        ],
      },
      {
        heading: 'Why ServiceApp ranks for small teams',
        paragraphs: [
          'ServiceApp.ai prices by office users, includes unlimited crew, and ships Ava for enquiries and follow-ups. Start on Start from $49/mo from the [service app](/service-app) page.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What is the best free service app?',
        a: 'Look for a free tier that still includes scheduling and invoicing. ServiceApp.ai offers a Start from $49/mo for owner-operators with a monthly job limit.',
      },
    ],
  },
  {
    slug: 'field-service-app-vs-fsm-software',
    title: 'Field Service App vs FSM Software: What’s the Difference?',
    description:
      'Field service app vs field service management (FSM) software explained for small businesses — and when a simpler field service app beats enterprise FSM.',
    keywords:
      'field service app, field service management software, FSM software, field service software for small business',
    datePublished: '2026-07-26',
    dateModified: '2026-08-03',
    category: 'Field service',
    readingMinutes: 8,
    heroImage: IMG_BANNER,
    heroImageAlt: 'Technician using a field service app on site',
    excerpt:
      'Enterprise FSM and a field service app for small teams solve related problems at very different prices and complexity. Here is how to choose.',
    relatedLandingSlugs: [
      '/field-service-app',
      '/field-service-software',
      '/field-service-management-software',
    ],
    relatedPostSlugs: ['best-service-app-for-small-business', 'mobile-job-scheduling-for-crews'],
    sections: [
      {
        heading: 'FSM software',
        paragraphs: [
          'Field service management software historically meant enterprise platforms: complex dispatch, asset history, long implementations and per-technician pricing. Great for 200-truck fleets; heavy for a five-person cleaning or HVAC team.',
        ],
      },
      {
        heading: 'Field service app for SMBs',
        paragraphs: [
          'A modern field service app focuses on scheduling, mobile job completion, quotes and payments — live in a day. ServiceApp.ai sits in this category while still covering CRM and invoicing end to end.',
          'Compare [field service app](/field-service-app), [field service software](/field-service-software) and [FSM software](/field-service-management-software) pages for positioning.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Do small businesses need enterprise FSM?',
        a: 'Usually no. If you need scheduling, mobile reports, quotes and invoicing without consultants, a field service app like ServiceApp.ai is the better fit.',
      },
    ],
  },
  {
    slug: 'cleaning-service-app-guide',
    title: 'Cleaning Service App Guide: Schedule Crews, Quote & Get Paid',
    description:
      'How a cleaning service app helps residential and commercial cleaners manage recurring jobs, WhatsApp quotes, checklists, invoicing and crew schedules.',
    keywords: 'cleaning service app, cleaning business app, cleaning service software, cleaning company app',
    datePublished: '2026-07-28',
    dateModified: '2026-08-03',
    category: 'Cleaning',
    readingMinutes: 9,
    heroImage: IMG_CLEAN,
    heroImageAlt: 'Cleaning business owner using a cleaning service app',
    excerpt:
      'Missed WhatsApp enquiries and Sunday-night invoicing are optional. A cleaning service app makes recurring work and payments boring — in a good way.',
    relatedLandingSlugs: ['/cleaning-service-app', '/cleaning-service-software', '/maid-service-software'],
    relatedPostSlugs: ['recurring-jobs-for-cleaning-businesses', 'whatsapp-quotes-that-convert'],
    sections: [
      {
        heading: 'What cleaners need from software',
        paragraphs: [
          'Recurring schedules, room or site checklists, crew calendars, photo proof, WhatsApp quotes for new homes and offices, and invoices the day the clean finishes. That is the cleaning service app checklist.',
        ],
      },
      {
        heading: 'Residential vs commercial',
        paragraphs: [
          'Residential teams lean on fortnightly repeats and easy customer booking. Commercial and janitorial teams need multi-site contracts and consolidated billing. ServiceApp.ai supports both — see [cleaning service app](/cleaning-service-app) and [cleaning service software](/cleaning-service-software).',
        ],
      },
    ],
    faqs: [
      {
        q: 'What is the best cleaning service app?',
        a: 'The best cleaning service app covers recurring jobs, crew dispatch, checklists, quotes and payments. ServiceApp.ai includes unlimited crew and a Start from $49/mo.',
      },
    ],
  },
  {
    slug: 'recurring-jobs-for-cleaning-businesses',
    title: 'How to Manage Recurring Cleaning Jobs Without Spreadsheets',
    description:
      'Set up weekly and monthly cleaning contracts, auto-schedule crews and stop double-booking — a practical guide for cleaning businesses.',
    keywords:
      'recurring cleaning jobs, cleaning scheduling software, maid scheduling, cleaning contracts software',
    datePublished: '2026-07-29',
    dateModified: '2026-08-03',
    category: 'Cleaning',
    readingMinutes: 7,
    heroImage: IMG_CLEAN,
    heroImageAlt: 'Calendar of recurring cleaning jobs',
    excerpt:
      'Recurring revenue dies when the calendar lives in someone’s head. Automate repeats and your retainers stop depending on memory.',
    relatedLandingSlugs: ['/cleaning-service-software', '/maid-service-software'],
    relatedPostSlugs: ['cleaning-service-app-guide', 'invoice-same-day-get-paid-faster'],
    sections: [
      {
        heading: 'Build the contract once',
        paragraphs: [
          'Capture frequency, duration, price, checklist and preferred crew. Good small business software turns that into calendar events automatically so you are not recreating next month by hand.',
        ],
      },
      {
        heading: 'Exceptions without chaos',
        paragraphs: [
          'Holidays, skip weeks and one-off deep cleans should be edits on the job — not a new spreadsheet tab. Crews see the change on their phone the same day.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can ServiceApp handle fortnightly cleans?',
        a: 'Yes. Recurring schedules support weekly, fortnightly and monthly patterns used by residential and commercial cleaning teams.',
      },
    ],
  },
  {
    slug: 'whatsapp-quotes-that-convert',
    title: 'WhatsApp Quotes That Convert: A Playbook for Service Businesses',
    description:
      'How to send WhatsApp quotations that customers open and confirm — templates, timing and why quoting in the chat they already use wins more jobs.',
    keywords: 'WhatsApp quotation, WhatsApp quotes for business, quote on WhatsApp, service business quoting',
    datePublished: '2026-07-30',
    dateModified: '2026-08-03',
    category: 'Sales',
    readingMinutes: 7,
    heroImage: IMG_BANNER,
    heroImageAlt: 'Sending a professional quote on WhatsApp',
    excerpt:
      'Email quotes die in spam. WhatsApp quotes get opened. Here is how to make them professional — and turn one tap into a booked job.',
    relatedLandingSlugs: ['/service-app', '/cleaning-service-software'],
    relatedPostSlugs: ['stop-losing-leads-after-hours', 'how-to-run-a-small-service-business'],
    sections: [
      {
        heading: 'Speed beats polish',
        paragraphs: [
          'A clear price within an hour beats a perfect PDF tomorrow. Use branded quote pages with line items, tax and a confirm button — then send the link on WhatsApp.',
        ],
      },
      {
        heading: 'Close the loop in software',
        paragraphs: [
          'When a customer confirms, the quote should become a scheduled job automatically. That is how ServiceApp.ai WhatsApp quotations work — no retyping into a calendar.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can customers pay from a WhatsApp quote?',
        a: 'Typically they confirm the quote into a booking, then pay from the invoice or payment link after (or before) the job — depending on how you configure deposits.',
      },
    ],
  },
  {
    slug: 'stop-losing-leads-after-hours',
    title: 'Stop Losing Leads After Hours with an AI Business Assistant',
    description:
      'Why service businesses lose jobs at night and how an AI assistant for small business replies, drafts quotes and follows up while you are on site.',
    keywords:
      'AI for small business, AI business assistant, AI assistant for service business, after hours leads',
    datePublished: '2026-08-01',
    dateModified: '2026-08-03',
    category: 'AI',
    readingMinutes: 8,
    heroImage: IMG,
    heroImageAlt: 'AI assistant handling enquiries after hours',
    excerpt:
      'Your competitors are not faster during the day — they just answer at 10pm. AI closes that gap without hiring a night receptionist.',
    relatedLandingSlugs: ['/service-app', '/small-business-software'],
    relatedPostSlugs: ['whatsapp-quotes-that-convert', 'best-small-business-software-2026'],
    sections: [
      {
        heading: 'The after-hours leak',
        paragraphs: [
          'Most service enquiries arrive when owners are driving, on a ladder or asleep. Unanswered WhatsApp messages become jobs for whoever replies first in the morning — often not you.',
        ],
      },
      {
        heading: 'What Ava does in ServiceApp',
        paragraphs: [
          'Ava uses your real services, pricing and availability to answer enquiries, draft quotes for approval and nudge quiet deals. It is AI for small business that does work — not another dashboard widget.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Will AI replace my receptionist?',
        a: 'For many small teams, AI covers nights and peaks so you do not need a full-time receptionist yet. Humans still approve edge-case quotes and handle complex customers.',
      },
    ],
  },
  {
    slug: 'invoice-same-day-get-paid-faster',
    title: 'Invoice the Same Day: How Service Businesses Get Paid Faster',
    description:
      'Same-day invoicing habits, payment links and automatic reminders that improve cash flow for cleaning, trades and field service businesses.',
    keywords:
      'get paid faster, same day invoicing, invoicing software for service business, payment links for contractors',
    datePublished: '2026-08-01',
    dateModified: '2026-08-03',
    category: 'Finance',
    readingMinutes: 7,
    heroImage: IMG,
    heroImageAlt: 'Sending an invoice with a payment link after a job',
    excerpt:
      'Cash flow problems are often invoicing problems in disguise. Finish the job, send the bill, attach a pay link — every time.',
    relatedLandingSlugs: ['/service-software', '/small-business-software'],
    relatedPostSlugs: [
      'how-to-run-a-small-service-business',
      'replace-spreadsheets-whatsapp-with-one-system',
    ],
    sections: [
      {
        heading: 'Make completion trigger the invoice',
        paragraphs: [
          'If invoicing depends on remembering, it will slip. Service software should generate a tax-ready invoice when a job is marked complete and send a payment link automatically.',
        ],
      },
      {
        heading: 'Remind without awkwardness',
        paragraphs: [
          'Automatic reminders beat personal chase texts. Keep tone professional, attach the original invoice and offer the same pay link again.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Should I take deposits?',
        a: 'For large or custom jobs, yes. For recurring cleans, many teams invoice on completion. Use whatever policy you can enforce consistently in software.',
      },
    ],
  },
  {
    slug: 'replace-spreadsheets-whatsapp-with-one-system',
    title: 'Replace Spreadsheets and WhatsApp with One Business System',
    description:
      'Why spreadsheets and WhatsApp break as you grow — and how one small business operating system keeps leads, jobs and money connected.',
    keywords:
      'replace spreadsheets, WhatsApp for business limits, small business operating system, business system of record',
    datePublished: '2026-08-02',
    dateModified: '2026-08-03',
    category: 'Operations',
    readingMinutes: 8,
    heroImage: IMG,
    heroImageAlt: 'Moving from spreadsheets to one business system',
    excerpt:
      'WhatsApp is a great messaging app and a terrible ERP. Spreadsheets are great analysis tools and terrible job schedulers. Here is the exit plan.',
    relatedLandingSlugs: ['/small-business-software', '/how-to-run-a-small-business', '/business-app'],
    relatedPostSlugs: ['best-small-business-software-2026', 'how-to-run-a-small-service-business'],
    sections: [
      {
        heading: 'Symptoms you have outgrown the stack',
        paragraphs: [
          'Double bookings, unpaid jobs nobody remembered to invoice, “who spoke to that customer?”, and pricing that lives in one person’s notes. Those are system failures, not people failures.',
        ],
      },
      {
        heading: 'The migration path',
        paragraphs: [
          'Move customers and open jobs first, then templates for services and prices, then train the team on today’s calendar only. ServiceApp.ai is built as that system of record — see [small business software](/small-business-software).',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can I keep WhatsApp?',
        a: 'Yes. Use WhatsApp to talk to customers — but let software own the quote, job and invoice records so nothing depends on chat history.',
      },
    ],
  },
  {
    slug: 'all-in-one-vs-best-of-breed-software',
    title: 'All-in-One vs Best-of-Breed Software for Small Business',
    description:
      'All-in-one small business software vs best-of-breed tools: trade-offs, integration cost and when a single platform wins for service companies.',
    keywords: 'all-in-one small business software, best of breed software, software stack for small business',
    datePublished: '2026-08-02',
    dateModified: '2026-08-03',
    category: 'Small business software',
    readingMinutes: 8,
    heroImage: IMG,
    heroImageAlt: 'Comparing all-in-one vs multiple business tools',
    excerpt:
      'Best-of-breed wins for enterprises with IT teams. Most small service businesses win with all-in-one — because integration work is unpaid work.',
    relatedLandingSlugs: ['/small-business-software', '/service-software'],
    relatedPostSlugs: ['how-to-choose-small-business-software', 'best-small-business-software-2026'],
    sections: [
      {
        heading: 'The hidden cost of “best tools”',
        paragraphs: [
          'Each specialist app needs setup, a login, a bill and a sync. When sync breaks, owners become the integration layer. That does not scale.',
        ],
      },
      {
        heading: 'When all-in-one is enough',
        paragraphs: [
          'If your workflow is quote → schedule → deliver → invoice, an all-in-one platform like ServiceApp.ai covers 90% of the need. Keep a separate accounting package for year-end filing if your accountant requires it.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is all-in-one software less powerful?',
        a: 'For SMB service workflows, modern all-in-one tools are more powerful in practice because data stays connected. Enterprise depth is irrelevant if your team will not use it.',
      },
    ],
  },
  {
    slug: 'mobile-job-scheduling-for-crews',
    title: 'Mobile Job Scheduling for Field Crews (That They Will Use)',
    description:
      'How to schedule field crews on mobile: clear job details, routes, checklists and updates without forcing app-store installs or complex FSM tools.',
    keywords: 'mobile job scheduling, crew scheduling app, field crew management, job scheduling software',
    datePublished: '2026-08-02',
    dateModified: '2026-08-03',
    category: 'Field service',
    readingMinutes: 7,
    heroImage: IMG_BANNER,
    heroImageAlt: 'Field crew viewing scheduled jobs on a phone',
    excerpt:
      'If technicians need a 40-minute training to see today’s jobs, they will keep calling the office. Keep mobile scheduling ruthlessly simple.',
    relatedLandingSlugs: ['/field-service-app', '/field-service-software'],
    relatedPostSlugs: ['field-service-app-vs-fsm-software', 'invoice-same-day-get-paid-faster'],
    sections: [
      {
        heading: 'What belongs on the phone',
        paragraphs: [
          'Address, time window, customer notes, checklist, photos and a way to mark complete. Everything else can stay in the office view.',
        ],
      },
      {
        heading: 'Browser beats app stores for many teams',
        paragraphs: [
          'ServiceApp.ai runs in the mobile browser so crews are not blocked by install policies or outdated APKs. Pair that with a desktop calendar for dispatchers.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can I assign jobs by zone?',
        a: 'Yes — organise your calendar and team assignments by area so drive time stays sane as you grow.',
      },
    ],
  },
  {
    slug: 'hvac-pest-control-software-basics',
    title: 'HVAC and Pest Control Software Basics for Growing Teams',
    description:
      'Core software needs for HVAC and pest control businesses: AMC schedules, treatment follow-ups, compliance reports, dispatch and invoicing.',
    keywords: 'HVAC service software, pest control software, AMC scheduling, treatment follow up software',
    datePublished: '2026-08-03',
    dateModified: '2026-08-03',
    category: 'Trades',
    readingMinutes: 8,
    heroImage: '/serviceapp-owner-hvac.jpg',
    heroImageAlt: 'HVAC technician schedule and service reports',
    excerpt:
      'AMC contracts and treatment follow-ups are recurring revenue — if your software remembers them. Here are the basics for HVAC and pest control teams.',
    relatedLandingSlugs: ['/hvac-service-software', '/pest-control-software', '/field-service-app'],
    relatedPostSlugs: ['mobile-job-scheduling-for-crews', 'field-service-app-vs-fsm-software'],
    sections: [
      {
        heading: 'HVAC: AMC + breakdowns',
        paragraphs: [
          'Preventative visits should auto-schedule from contract start dates. Breakdown calls need fast dispatch. Technicians need history on site — last service, parts, warranty notes.',
        ],
      },
      {
        heading: 'Pest control: follow-ups and proof',
        paragraphs: [
          'Treatments often require return visits. Software should remind you, capture chemicals or methods used and send a professional report. See [HVAC service software](/hvac-service-software) and [pest control software](/pest-control-software).',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can one platform do HVAC and pest control?',
        a: 'Yes. ServiceApp.ai configures services and workflows per organisation, so multi-trade or specialised teams can use the same platform.',
      },
    ],
  },
  {
    slug: 'unlimited-team-no-per-seat-pricing',
    title: 'Why Per-Seat Pricing Hurts Small Service Businesses',
    description:
      'Per-seat software pricing taxes hiring. Learn why unlimited crew / office-user pricing is fairer for cleaning, trades and field service companies.',
    keywords:
      'per seat pricing, unlimited crew software, office-user pricing, software pricing for small business',
    datePublished: '2026-08-03',
    dateModified: '2026-08-03',
    category: 'Pricing',
    readingMinutes: 6,
    heroImage: IMG,
    heroImageAlt: 'Software pricing comparison for small teams',
    excerpt:
      'Every new cleaner or technician should grow revenue — not your SaaS bill. That is why ServiceApp never charges per seat.',
    relatedLandingSlugs: ['/small-business-software', '/service-app'],
    relatedPostSlugs: ['best-small-business-software-2026', 'how-to-choose-small-business-software'],
    sections: [
      {
        heading: 'The per-seat trap',
        paragraphs: [
          'Per-user pricing made sense for office SaaS. For field teams, it discourages putting everyone on the system — which defeats the purpose of a shared schedule.',
        ],
      },
      {
        heading: 'Office-user alternative',
        paragraphs: [
          'Pay for office users. ServiceApp.ai includes unlimited crew on every plan, including the Start from $49/mo, so software cost tracks activity.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Are crew logins really free?',
        a: 'Yes on ServiceApp plans — you are limited by job volume tiers, not by how many people log in.',
      },
    ],
  },
  {
    slug: 'solo-owner-operating-system',
    title: 'The Solo Owner Operating System: Run the Business from Your Phone',
    description:
      'How solo owners and tiny partnerships run sales, scheduling and invoicing from a phone between jobs — without hiring a full back office.',
    keywords:
      'solo business software, owner operator software, run business from phone, small partnership software',
    datePublished: '2026-08-03',
    dateModified: '2026-08-03',
    category: 'Operations',
    readingMinutes: 7,
    heroImage: IMG_BANNER,
    heroImageAlt: 'Solo business owner running operations from a phone',
    excerpt:
      'When you are sales, dispatch and accounts, the software has to be the back office. Here is the minimal operating system for solo owners.',
    relatedLandingSlugs: ['/how-to-run-a-small-business', '/business-app', '/service-app'],
    relatedPostSlugs: ['stop-losing-leads-after-hours', 'invoice-same-day-get-paid-faster'],
    sections: [
      {
        heading: 'Between-job admin',
        paragraphs: [
          'Quote from the driveway, book the next slot, mark today’s job complete and send the payment link before you drive off. If any of those require a laptop at night, the system is wrong.',
        ],
      },
      {
        heading: 'Partners on one picture',
        paragraphs: [
          'Two or three partners need the same live view of jobs and cash. Shared software beats “I’ll update the sheet later.” ServiceApp.ai is built for that owner-operator reality.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is ServiceApp good for one-person businesses?',
        a: 'Yes. The Start from $49/mo targets owner-operators, and Ava covers enquiries when you cannot answer the phone.',
      },
    ],
  },
  {
    slug: 'service-business-crm-basics',
    title: 'Service Business CRM Basics: Leads, Customers and Job History',
    description:
      'What a service business CRM should include beyond contacts: enquiry pipeline, quote history, job notes and payment status in one customer record.',
    keywords: 'service business CRM, CRM for service company, small business CRM for service companies',
    datePublished: '2026-08-03',
    dateModified: '2026-08-03',
    category: 'CRM',
    readingMinutes: 7,
    heroImage: IMG,
    heroImageAlt: 'Customer record with jobs and invoices in a service CRM',
    excerpt:
      'A stack of phone numbers is not a CRM. Service businesses need the full story: every quote, visit, complaint and payment on one timeline.',
    relatedLandingSlugs: ['/service-business-app', '/small-business-software'],
    relatedPostSlugs: ['what-is-a-service-app', 'replace-spreadsheets-whatsapp-with-one-system'],
    sections: [
      {
        heading: 'CRM that matches field work',
        paragraphs: [
          'Contacts, sites/addresses, open enquiries, quotes, scheduled jobs, service reports and invoices should link. When a customer calls, you see the last visit without digging through chat.',
        ],
      },
      {
        heading: 'Pipeline hygiene',
        paragraphs: [
          'Every lead needs a stage and owner. AI follow-ups help, but humans should still close or disqualify stale deals weekly so the pipeline stays truthful.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Do I need Salesforce for a cleaning company?',
        a: 'Almost never. A service-focused CRM inside your operating system is faster to adopt and already connected to jobs and invoices.',
      },
    ],
  },
  {
    slug: 'facility-and-multi-site-service-ops',
    title: 'Multi-Site Service Operations: Facility and Janitorial Teams',
    description:
      'How facility management and janitorial teams run multi-site contracts: SLAs, crew rotation, consolidated billing and reporting without enterprise complexity.',
    keywords:
      'facility management software, janitorial software, multi-site service software, commercial cleaning software',
    datePublished: '2026-08-03',
    dateModified: '2026-08-03',
    category: 'Facility',
    readingMinutes: 8,
    heroImage: IMG,
    heroImageAlt: 'Multi-site facility contracts on a service dashboard',
    excerpt:
      'Multi-site work fails in spreadsheets. Contracts, SLAs and consolidated invoices need one platform — even if you are not an enterprise FM firm.',
    relatedLandingSlugs: [
      '/facility-management-software',
      '/janitorial-software',
      '/cleaning-service-software',
    ],
    relatedPostSlugs: ['cleaning-service-app-guide', 'mobile-job-scheduling-for-crews'],
    sections: [
      {
        heading: 'One client, many sites',
        paragraphs: [
          'Model buildings under a parent customer. Schedule crews per site, but bill together when the contract says so. Photo reports per visit keep SLAs defensible.',
        ],
      },
      {
        heading: 'Right-sized software',
        paragraphs: [
          'You do not need a global FM suite to run twenty buildings. ServiceApp.ai covers multi-site dispatch and billing for growing contractors — see [facility management software](/facility-management-software) and [janitorial software](/janitorial-software).',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can subcontractors appear in the same schedule?',
        a: 'Track internal and external teams carefully. Use clear assignment and completion records so the client still gets one professional report.',
      },
    ],
  },
];

export const SERVICEAPP_BLOG_POSTS: ServiceAppBlogPost[] = [
  ...SERVICEAPP_BLOG_POSTS_CORE,
  ...SERVICEAPP_BLOG_POSTS_EXTRA,
];

export function getServiceAppBlogPostBySlug(slug: string): ServiceAppBlogPost | undefined {
  const normalized = slug.replace(/^\/+|\/+$/g, '');
  return SERVICEAPP_BLOG_POSTS.find((p) => p.slug === normalized);
}

export function getServiceAppBlogPath(slug: string): string {
  return `/blog/${slug.replace(/^\/+|\/+$/g, '')}`;
}

export function listVisibleServiceAppBlogPosts(hiddenSlugs: string[] = []): ServiceAppBlogPost[] {
  const hidden = new Set(
    hiddenSlugs
      .map((item) => item.trim().replace(/^\/+|\/+$/g, '').replace(/^blog\//, ''))
      .filter(Boolean),
  );
  return SERVICEAPP_BLOG_POSTS.filter((post) => !hidden.has(post.slug));
}

export function listServiceAppBlogCategories(posts: ServiceAppBlogPost[]): string[] {
  return [...new Set(posts.map((post) => post.category))];
}

export function sortBlogPostsNewestFirst(posts: ServiceAppBlogPost[]): ServiceAppBlogPost[] {
  return [...posts].sort((a, b) => (a.datePublished < b.datePublished ? 1 : a.datePublished > b.datePublished ? -1 : 0));
}

export const SERVICEAPP_BLOG_PATHS = [
  '/blog',
  ...SERVICEAPP_BLOG_POSTS.map((p) => getServiceAppBlogPath(p.slug)),
];
